import { useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Return type for useScopes hook
 */
export interface UseScopesReturn {
  /**
   * Array of granted scopes
   */
  scopes: string[];

  /**
   * Check if user has a specific scope
   */
  hasScope: (scope: string) => boolean;

  /**
   * Check if user has any of the specified scopes
   */
  hasAnyScope: (scopes: string[]) => boolean;

  /**
   * Check if user has all of the specified scopes
   */
  hasAllScopes: (scopes: string[]) => boolean;
}

/**
 * Hook for checking OAuth 2.0 scopes
 *
 * Provides utilities for scope-based authorization. Scopes are typically space-separated
 * in the token (e.g., "openid profile email read:users write:users").
 *
 * @returns Scope checking utilities
 *
 * @example Basic usage
 * ```tsx
 * import { useScopes } from 'antaeus.keycloak.react';
 *
 * function MyComponent() {
 *   const { scopes, hasScope, hasAnyScope } = useScopes();
 *
 *   return (
 *     <div>
 *       <p>Your scopes: {scopes.join(', ')}</p>
 *
 *       {hasScope('read:users') && <UserList />}
 *
 *       {hasAnyScope(['write:users', 'admin']) && <CreateUserButton />}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example Conditional rendering
 * ```tsx
 * function DataActions() {
 *   const { hasScope, hasAllScopes } = useScopes();
 *
 *   return (
 *     <div>
 *       {hasScope('read:data') && <ViewButton />}
 *       {hasScope('write:data') && <EditButton />}
 *       {hasScope('delete:data') && <DeleteButton />}
 *       {hasAllScopes(['read:data', 'write:data']) && <ExportButton />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useScopes(): UseScopesReturn {
  const auth = useAuth();

  // Extract scopes from token
  // Scopes can be in 'scope' claim (space-separated string)
  const scopes = useMemo<string[]>(() => {
    if (!auth.user?.scope) return [];

    // Scope is typically a space-separated string
    return auth.user.scope.split(' ').filter(s => s.length > 0);
  }, [auth.user]);

  // Check if user has a specific scope
  const hasScope = useMemo(
    () => (scope: string): boolean => {
      return scopes.includes(scope);
    },
    [scopes]
  );

  // Check if user has any of the specified scopes
  const hasAnyScope = useMemo(
    () => (requiredScopes: string[]): boolean => {
      return requiredScopes.some((scope) => scopes.includes(scope));
    },
    [scopes]
  );

  // Check if user has all of the specified scopes
  const hasAllScopes = useMemo(
    () => (requiredScopes: string[]): boolean => {
      return requiredScopes.every((scope) => scopes.includes(scope));
    },
    [scopes]
  );

  return {
    scopes,
    hasScope,
    hasAnyScope,
    hasAllScopes,
  };
}
